/**
 * Jovii — DOWNLOADS: the manager. One queue, one runner, one file at a time.
 *
 * The reactive face of `index-state.ts` and `store.ts`. Screens read
 * `downloads.items` and stay reactive; everything they can do to a download is a
 * function here.
 *
 * ── WHERE THE BYTES COME FROM ──────────────────────────────────────────────────
 *
 * The SAME source the player would pick, resolved through the SAME code path:
 * `api.resolve` for the candidate list, `playOrder` for this browser's ordering,
 * and `probeCandidate` (picker.svelte.ts) for the 16 KB verification that decides
 * whether a link is a film or a `failed_opening_v2.mp4` decoy. Downloading an
 * unverified candidate would fill a gigabyte of OPFS with an HTML error page, and
 * the picker already knows how to tell them apart — reusing it also means a
 * candidate verified for playback a minute ago is not re-probed here, because
 * the verdict cache is shared.
 *
 * The probe also supplies the one number the quota check needs: `sizeBytes`, read
 * off a real Content-Range rather than the aggregator's claim, which is wrong for
 * 100 of 237 sampled candidates and always wrong for a season pack.
 *
 * ── ONE AT A TIME, AND WHY ─────────────────────────────────────────────────────
 *
 * `nextInQueue` documents the argument. In short: ten parallel whole-file reads
 * through one Real-Debrid account share one account's throughput, so a season
 * downloaded in parallel finishes episode one LAST. Serial means the first
 * episode is watchable while the rest arrive.
 *
 * ── A RELOAD IS NOT A LOSS ─────────────────────────────────────────────────────
 *
 * Every completed chunk is on disk before the index is written, and the resume
 * offset is the FILE's length, never the index's number (see `index-state.ts`).
 * A tab that dies at 3.2 GB restarts at 3.2 GB, minus at most one 4 MB chunk.
 */

import type { Candidate, ContentRef, ProbedSource } from '$types';
import { KEYS, readJson, writeJson } from '$lib/ui/storage';
import * as api from '$lib/ui/api';
import { playOrder, probeCandidate } from '$lib/ui/picker.svelte';
/* The audio a download gets is the audio a Play press would have got: the same
   function, reading the same saved preference. An episode taken on a flight must
   not turn out to be in the release's default language. */
import { preferredAudioAvailability } from '$lib/ui/quality';
import { prefs } from '$lib/ui/prefs.svelte';
// The player's own renewal path, for a provider URL that expires mid-download.
import { renewCandidateUrl } from '$lib/player/resolve';
import {
  CHUNK_BYTES,
  decodeIndex,
  downloadKey,
  nextInQueue,
  quotaVerdict,
  reconcile,
  resumeOffset,
  upsert,
  withoutKey
} from './index-state';
import * as store from './store';
import * as destination from './destination';
import { keepPoster } from '$lib/ui/poster-bytes';
import type { RemuxEvent } from './remux-protocol';
import type { DownloadEntry } from './types';

/* ────────────────────────────────────────────────────────────────────────────
 * State
 * ──────────────────────────────────────────────────────────────────────────── */

export const downloads: { items: DownloadEntry[]; ready: boolean; notice: string } = $state({
  items: readJson<DownloadEntry[]>(KEYS.downloads, [], decodeIndex),
  /** True once the boot reconciliation against OPFS has finished. */
  ready: false,
  /** The last thing that needs saying out loud — a refusal, usually. Read and cleared. */
  notice: ''
});

function persist(): void {
  writeJson(KEYS.downloads, $state.snapshot(downloads.items));
}

function put(entry: DownloadEntry): void {
  downloads.items = upsert(downloads.items, entry);
  persist();
}

function patch(key: string, changes: Partial<DownloadEntry>): DownloadEntry | null {
  const current = downloads.items.find((e) => e.key === key);
  if (!current) return null;
  const next = { ...current, ...changes };
  downloads.items = upsert(downloads.items, next);
  persist();
  return next;
}

export function entryFor(key: string): DownloadEntry | undefined {
  return downloads.items.find((e) => e.key === key);
}

/** The one question the player asks: is this thing here, whole? */
/**
 * THE FILE ON DISK WOULD NOT PLAY. Mark it so, from the player.
 *
 * Called when playback from a download fails (controller.svelte.ts): the entry
 * stops claiming to be `done`, so the Downloads screen offers it again instead
 * of showing a tick beside something that cannot be watched. The BYTES are left
 * alone - `bytes` is still the resume offset, and a retry continues rather than
 * starting the film again. A missing entry is not an error: the index may have
 * been cleared between the play and the failure.
 */
export function markDownloadFailed(
  ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>,
  reason: string
): void {
  const key = downloadKey(ref);
  if (!entryFor(key)) return;
  patch(key, { state: 'failed', error: reason });
}

export function completedDownload(
  ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>
): DownloadEntry | null {
  const found = entryFor(downloadKey(ref));
  return found && found.state === 'done' && found.size > 0 && found.bytes >= found.size ? found : null;
}

export function say(message: string): void {
  downloads.notice = message;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Boot
 * ──────────────────────────────────────────────────────────────────────────── */

let booted = false;

/**
 * Reconcile the index against what is actually on disk, then carry on with
 * whatever was in flight when the tab last closed.
 *
 * Idempotent, and called from every screen that shows a download, because the
 * Downloads card on Profile is often the first thing mounted in a session.
 */
export async function bootDownloads(): Promise<void> {
  if (booted) return;
  booted = true;
  if (!store.supported()) {
    downloads.ready = true;
    return;
  }
  void store.requestPersistence();
  const reconciled: DownloadEntry[] = [];
  for (const entry of downloads.items) {
    /*
     * A DELIVERED FILE IS FINISHED, whatever the staging area says.
     *
     * `reconcile` trusts the file on disk over the index, which is right while
     * a download is being assembled and WRONG afterwards: on the 'save'
     * strategy the staged copy is swept once the browser has taken it, and a
     * reconcile against the missing bytes would put a finished download back
     * to `queued` and download it all over again.
     */
    if (entry.state === 'done' && entry.savedVia === 'save') {
      reconciled.push(entry);
      continue;
    }
    reconciled.push(reconcile(entry, await store.fileBytes(entry.key)));
  }
  downloads.items = reconciled;
  persist();
  downloads.ready = true;
  void pump();
  /* Reclaim staged copies the browser has finished with. See sweepDelivered. */
  void sweepDelivered();
}

/* ────────────────────────────────────────────────────────────────────────────
 * Enqueueing
 * ──────────────────────────────────────────────────────────────────────────── */

export interface EnqueueInput {
  ref: ContentRef;
  /** Series name, or the film's title. */
  title: string;
  episodeLabel?: string;
  poster?: string;
}

/**
 * Put one thing in the queue. A no-op when it is already there in any state —
 * pressing the arrow on a queued episode must not create a second row for one
 * file. Returns the key so a caller can watch it.
 */
export function enqueue(input: EnqueueInput): string {
  const key = downloadKey(input.ref);
  const existing = entryFor(key);
  if (existing && existing.state !== 'failed') return key;

  const entry: DownloadEntry = {
    key,
    ref: input.ref,
    title: input.title,
    bytes: existing?.bytes ?? 0,
    size: existing?.size ?? 0,
    state: 'queued',
    addedAt: existing?.addedAt ?? Date.now()
  };
  if (input.episodeLabel) entry.episodeLabel = input.episodeLabel;
  if (input.poster) entry.poster = input.poster;
  put(entry);
  /* The poster goes to the disk with the film, so the row has artwork on a
     plane. Fire-and-forget: a poster that fails to fetch is the row's initial.
     AFTER put(): a patch of mine once landed this line on the guard's early
     return above and every download on prod became a no-op for a deploy. */
  if (input.poster) void keepPoster(key, input.poster);
  void pump();
  return key;
}

/**
 * A whole season, in episode order.
 *
 * `addedAt` is stamped ascending rather than all at `Date.now()`: the queue is
 * oldest-first, and ten rows written inside the same millisecond have no order
 * at all — which showed up as a season downloading episode 7 first.
 */
export function enqueueMany(inputs: EnqueueInput[]): void {
  const base = Date.now();
  inputs.forEach((input, i) => {
    const key = downloadKey(input.ref);
    const existing = entryFor(key);
    if (existing && existing.state !== 'failed') return;
    const entry: DownloadEntry = {
      key,
      ref: input.ref,
      title: input.title,
      bytes: existing?.bytes ?? 0,
      size: existing?.size ?? 0,
      state: 'queued',
      addedAt: base + i
    };
    if (input.episodeLabel) entry.episodeLabel = input.episodeLabel;
    if (input.poster) entry.poster = input.poster;
    downloads.items = upsert(downloads.items, entry);
  });
  persist();
  void pump();
}

/**
 * Stop this one. Bytes already written STAY: a cancel is "not now", and throwing
 * away 3 GB because somebody's thumb landed on the wrong row is not a thing this
 * app should be able to do. `remove()` is the one that deletes.
 */
export function cancel(key: string): void {
  if (running === key) {
    runAbort?.abort();
    runAbort = null;
    running = null;
  }
  const entry = entryFor(key);
  if (entry && entry.state !== 'done') patch(key, { state: 'queued' });
}

/** Cancel if running, then delete the row and its file. */
export async function remove(key: string): Promise<void> {
  if (running === key) {
    runAbort?.abort();
    runAbort = null;
    running = null;
  }
  downloads.items = withoutKey(downloads.items, key);
  persist();
  await store.removeFile(key);
  void pump();
  await store.removePoster(key);
}

/** Everything, gone. The "Delete all" behind the Downloads menu. */
export async function removeEverything(): Promise<void> {
  runAbort?.abort();
  runAbort = null;
  running = null;
  downloads.items = [];
  persist();
  await store.removeAll();
}

/** The arrow pressed on a row that is already doing something: the toggle. */
export function toggle(key: string): void {
  const entry = entryFor(key);
  if (!entry) return;
  if (entry.state === 'downloading' || entry.state === 'queued') cancel(key);
  else if (entry.state === 'failed') {
    patch(key, { state: 'queued' });
    void pump();
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The runner
 * ──────────────────────────────────────────────────────────────────────────── */

/** The key being worked on right now, or null. At most one, ever. */
let running: string | null = null;
let runAbort: AbortController | null = null;

/** Start the next queued item if nothing is running. Safe to call at any time. */
export async function pump(): Promise<void> {
  if (running !== null || !store.supported()) return;
  const next = nextInQueue(downloads.items);
  if (!next) return;
  running = next.key;
  const abort = new AbortController();
  runAbort = abort;
  try {
    await run(next.key, abort.signal);
  } catch (err) {
    if (!abort.signal.aborted) {
      patch(next.key, { state: 'failed', error: message(err) });
    }
  } finally {
    if (running === next.key) {
      running = null;
      runAbort = null;
    }
  }
  // Whatever happened, the queue may still have work — including the episode
  // behind the one that just failed, which must not be blocked by it.
  if (!abort.signal.aborted) void pump();
}

/**
 * WHY IT STOPPED, IN WORDS A PERSON WOULD USE.
 *
 * Not cosmetic. The raw text here comes from `api.resolve`, which forwards the
 * SERVICE's own diagnostics — a misconfigured instance answers 503 with
 * "TOGETHER_AIOSTREAMS_URL is not set", and that string went straight onto a
 * download row under a poster, which is an operator's environment variable shown
 * to somebody who wanted an episode on a train. Found in a screenshot of the
 * real screen, not in review.
 *
 * The shapes that have a human meaning get one. Anything unrecognised is passed
 * through but capped: an unknown failure with its real text is worth more to
 * whoever has to debug it than a friendly sentence that hides it.
 */
function message(err: unknown): string {
  const text = (err instanceof Error ? err.message : String(err)).trim();
  if (/AIOSTREAMS|not configured|no link source/i.test(text)) {
    return 'This Jovii has no link source configured, so there is nothing to download yet.';
  }
  if (/^No links/i.test(text)) return 'No links for this yet.';
  if (/would verify/i.test(text)) return 'None of the links for this would play, so nothing was downloaded.';
  if (/aborted|AbortError/i.test(text)) return 'Stopped.';
  if (/NetworkError|Failed to fetch|networkerror/i.test(text)) {
    return 'The connection dropped. It will carry on from here when you try again.';
  }
  if (!text) return 'The download stopped.';
  return text.length > 140 ? `${text.slice(0, 137)}…` : text;
}

/**
 * Find the candidate this download should read, verified.
 *
 * Deliberately the same three calls the watch screen makes, in the same order:
 * resolve → playOrder → probeCandidate. A candidate already verified for playback
 * is returned out of the picker's shared verdict cache without a second probe,
 * and the `direct` capability the probe carries is what routes the bytes
 * provider-to-browser below.
 */
async function pickSource(
  entry: DownloadEntry,
  signal: AbortSignal
): Promise<{ candidate: Candidate; probe: ProbedSource & { direct?: unknown } }> {
  const result = await api.resolve(entry.ref, { signal });
  if (result.candidates.length === 0) throw new Error('No links for this yet.');
  const ordered = playOrder(entry.ref, result.candidates);

  // The one it used last time, first: a resume must append to the SAME file, and
  // a different release is a different film's worth of bytes at the same offset.
  const preferred = entry.candidateId
    ? [...ordered.filter((c) => c.id === entry.candidateId), ...ordered.filter((c) => c.id !== entry.candidateId)]
    : ordered;

  /*
   * A DOWNLOAD WANTS A DIFFERENT WINNER FROM A STREAM, and this is the one
   * place the two part company.
   *
   * `playOrder` ranks candidates for playing HERE, NOW, in THIS browser — and
   * a browser that can decode AV1 is perfectly happy to be handed an AV1
   * release. A file is not for here and now. It is going to the Files app on a
   * phone, and Apple's player decodes AV1 only on the most recent hardware; on
   * everything else the file opens, shows a black rectangle and plays the
   * sound. That is the same failure `planAudio` refuses for E-AC-3 — legal in
   * MP4, silent or blank on the device it was downloaded to — and the video
   * side needs the same rule, because the remuxer COPIES video byte for byte
   * and cannot fix it afterwards.
   *
   * Found by QA on 2026-09-14: Silo S1E1 downloaded as a structurally perfect
   * 802 MB MP4 whose video stream was AV1, while eleven H.264 candidates for
   * the same episode sat further down the same list.
   *
   * So the walk is TWO PASSES. The first takes only a device-safe video codec.
   * The second accepts whatever verifies, because a file the phone struggles
   * with still beats no file at all for anyone reading it on a laptop — and a
   * release that exists only in AV1 must not become undownloadable.
   */
  const verified: { candidate: Candidate; probe: ProbedSource }[] = [];
  for (const candidate of preferred.slice(0, 8)) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const status = await probeCandidate(candidate, { signal });
    if (status.state !== 'good' || status.probe.sizeBytes <= 0) continue;
    if (devicePlayableVideo(status.probe)) return { candidate, probe: status.probe };
    verified.push({ candidate, probe: status.probe });
  }
  const fallback = verified[0];
  if (fallback) return fallback;
  throw new Error('None of the links for this would verify.');
}

/**
 * Will Apple's own player decode this file's video?
 *
 * H.264 and HEVC, and nothing else. Both are hardware-decoded on every iPhone
 * and iPad the owner could be holding; AV1 is not (A17 Pro and later only), VP9
 * is not in an MP4 at all, and MPEG-4 Part 2 belongs to another decade.
 *
 * Deliberately a WHITELIST rather than a list of refusals: a codec nobody here
 * has heard of is a codec nobody has checked, and the failure it produces is a
 * black rectangle on a plane.
 */
function devicePlayableVideo(probe: ProbedSource): boolean {
  const video = probe.tracks.filter((t) => t.kind === 'video');
  return video.length > 0 && video.every((t) => t.codec === 'h264' || t.codec === 'hevc');
}

/**
 * THE BYTES TRAVEL EXACTLY AS PLAYBACK'S DO, and this is a hard constraint
 * rather than a convenience.
 *
 * A download is read through `RangeReader` with the same `DirectRangeTransport`
 * the media engine uses, so the bytes go PROVIDER → BROWSER over HTTPS byte
 * ranges and the server carries none of them. That is the contract in
 * `lib/media/README.md` ("Media travels directly from the enabled provider to
 * the viewer"), and a download that fetched the byte proxy instead would quietly
 * push a 12 GB relay through a service whose whole design is that it forwards
 * headers and not films.
 *
 * The only Jovii requests in this whole path are the ones a Play press already
 * makes: `/api/resolve` for the candidate list and `/api/playback/direct` for
 * the capability, both inside code the player owns. There is no download route,
 * no server-side queue and no server-side copy of anything.
 *
 * REUSING THE READER BUYS THE REST FOR FREE, too: the concurrency cap, the
 * transient-failure retries and — the one that matters over the hour a film
 * takes — the expiry handling. A provider URL that dies at 40 minutes is renewed
 * against the same capability token and the identical byte range is re-read,
 * which is precisely what `RangeReader` was built to do and precisely what a
 * hand-rolled fetch loop would have had to reimplement badly.
 */
async function run(key: string, signal: AbortSignal): Promise<void> {
  const entry = entryFor(key);
  if (!entry) return;
  patch(key, { state: 'downloading' });

  const { candidate, probe } = await pickSource(entry, signal);
  if (signal.aborted) return;
  const sizeBytes = probe.sizeBytes;

  /*
   * WHERE THIS FILE IS GOING (destination.ts has the argument).
   *
   * 'folder': the reader picked a directory and the bytes stream straight into
   * it — no staging copy, no browser quota, and a season lands in
   * `<picked>/Silo/Season 1/`. 'save': there is no such API on this platform,
   * so the file is staged in private storage while it downloads and handed to
   * the browser's own download manager at the end.
   */
  const how = destination.strategy();
  const folder = how === 'folder' ? await destination.currentFolder() : null;
  /* `episodeLabel` is already "S1 E2 · Tackle Tommy Woo Woo", so passing it as
     the episode TITLE produced "The Gentlemen - S01E02 - S1 E2 · Tackle Tommy
     Woo Woo.mp4" on the owner's phone. Only the episode's own name belongs
     here; fileName() adds the code itself. */
  const episodeTitle = entry.episodeLabel?.replace(/^S\d+\s*E\d+\s*[·\-–]\s*/, '');
  const name = destination.fileName(entry.title, entry.ref.season, entry.ref.episode, episodeTitle);
  const into = folder ? await destination.folderFor(folder, entry.title, entry.ref.season) : null;
  const savedPath = entry.ref.season !== undefined
    ? `${destination.safeName(entry.title)}/Season ${entry.ref.season}`
    : '';

  if (how === 'folder' && !folder) {
    const message = 'Choose where to keep downloads, then press download again.';
    patch(key, { state: 'failed', error: message });
    say(message);
    return;
  }

  /*
   * REMUX, OR COPY?
   *
   * A Matroska source is converted (see `remux.ts`): iOS cannot open a .mkv,
   * and every one of these releases is one. A source that is ALREADY an MP4
   * with codecs the device plays needs nothing doing to it — copying its bytes
   * is both correct and faster than taking it apart and putting it back
   * together, and a byte copy is also the only path that can resume.
   *
   * Everything else (an MP4 with E-AC-3 in it, say) goes through the remuxer
   * too: the container being right does not make the audio playable.
   */
  const copyable = canCopyStraight(probe);

  const landing: Landing = { how, into, name, savedPath, sizeBytes };

  if (!copyable) {
    await remux(key, entry, candidate, probe, landing, signal);
    return;
  }

  // The file is the truth. Ask it, not the index (see index-state.ts).
  const onDisk = into
    ? await store.folderFileBytes(into, name)
    : ((await store.fileBytes(key)) ?? 0);
  const changedRelease = entry.candidateId !== undefined && entry.candidateId !== candidate.id;
  const startAt = changedRelease ? 0 : resumeOffset(onDisk, sizeBytes);

  if (startAt >= sizeBytes && sizeBytes > 0) {
    patch(key, {
      state: 'done', bytes: sizeBytes, size: sizeBytes, candidateId: candidate.id,
      savedVia: how, savedAs: name, ...(savedPath ? { savedPath } : {}), savedAt: Date.now()
    });
    return;
  }

  /*
   * The quota check applies to the STAGING strategy only. Writing into a
   * folder the reader chose is not subject to the origin's storage grant at
   * all — that is the whole reason a 12 GB film works there and does not here.
   */
  if (how === 'save') {
    // Only the bytes still MISSING have to fit; a resume of a 12 GB film with 11 GB
    // already here must not be refused for wanting 12.
    const verdict = quotaVerdict(await store.estimate(), sizeBytes - startAt);
    if (!verdict.fits) {
      patch(key, { state: 'failed', error: verdict.message });
      say(verdict.message);
      return;
    }
  }

  patch(key, { size: sizeBytes, bytes: startAt, candidateId: candidate.id, state: 'downloading' });

  const opened = into
    ? await store.openInFolder(into, name, startAt)
    : null;
  const writable = opened ? opened.stream : await store.openAppend(key, startAt);
  if (!writable) throw new Error('Jovii could not open that file to write to.');

  const { RangeReader } = await import('$lib/media/range-reader');
  const { createDirectTransport } = await import('$lib/media/direct-transport');
  const direct = (probe as { direct?: unknown }).direct !== undefined;
  const reader = new RangeReader({
    token: candidate.url,
    // Provider-direct when the probe negotiated a capability, exactly as the
    // media worker decides it (`media.worker.ts`, same ternary).
    ...(direct ? { directTransport: createDirectTransport() } : {}),
    // ONE connection, and no read-ahead. A download is background work behind
    // whatever somebody is actually watching, and the six-wide window the player
    // opens exists to reach a first frame quickly — a file that will take forty
    // minutes either way has no such deadline and should not take six of a
    // provider's per-connection slots to meet it.
    maxConcurrent: 1,
    readAheadChunks: 1,
    chunkBytes: CHUNK_BYTES,
    fileSize: sizeBytes,
    // The link expiring mid-download is the NORMAL case over an hour, not an
    // error: re-resolve the same candidate and the reader retries the identical
    // range against the fresh URL.
    onTokenExpired: () => renewCandidateUrl(entry.ref, candidate.id)
  });

  let at = startAt;
  try {
    while (at < sizeBytes) {
      if (signal.aborted) return;
      const end = Math.min(at + CHUNK_BYTES, sizeBytes);
      const chunk = await reader.fetchRange(at, end, signal);
      if (signal.aborted) return;
      if (chunk.byteLength === 0) throw new Error('The link stopped sending bytes.');
      // `Uint8Array<ArrayBufferLike>` is what the reader's type says, and OPFS's
      // write() takes only a non-shared view. The reader never produces a shared
      // buffer — every chunk comes from a fetch body — so this narrows a type,
      // not a value, and copying the 4 MB to prove it would be 4 MB per chunk.
      await writable.write(chunk as Uint8Array<ArrayBuffer>);
      at += chunk.byteLength;
      /*
       * THE GRANT MOVES WHILE WE WRITE. Re-read it every 16 chunks (~64 MB)
       * and stop cleanly before the browser throws, because its own throw
       * lands mid-write and says only "would exceed the storage quota". The
       * bytes already written stay valid as a resume offset, so deleting
       * something and pressing again continues from here.
       */
      if (how === 'save' && at % (CHUNK_BYTES * 16) < CHUNK_BYTES) {
        const room = quotaVerdict(await store.estimate(), sizeBytes - at);
        if (!room.fits) {
          patch(key, { state: 'failed', error: room.message });
          say(room.message);
          return;
        }
      }
      // The index follows the file, one chunk behind at worst. Written every
      // chunk rather than at the end, because "at the end" is exactly the write
      // a tab that dies never performs.
      patch(key, { bytes: Math.min(at, sizeBytes) });
      // Nothing is cached for a download: every chunk is read once, written to
      // disk and never wanted again, so the reader's window would be pure
      // memory growth across a 12 GB file.
      reader.trimCache(at);
    }
  } finally {
    reader.close();
    // Closing is what flushes. An aborted run closes too — the bytes it did
    // write are the resume offset and must actually be on disk.
    try {
      await writable.close();
    } catch {
      /* a stream aborted mid-write is already closed */
    }
  }

  if (signal.aborted) return;
  const finalBytes = into ? await store.folderFileBytes(into, name) : ((await store.fileBytes(key)) ?? at);
  await finish(key, candidate.id, landing, finalBytes >= sizeBytes && sizeBytes > 0, finalBytes);
}

/**
 * IS A STRAIGHT BYTE COPY CORRECT FOR THIS SOURCE?
 *
 * Only when the container is already MP4 AND every track in it is one the
 * device plays: H.264 or HEVC video, AAC audio. That combination needs nothing
 * doing to it, and copying is both faster than a remux and the only path that
 * can resume a half-finished file.
 *
 * The audio clause is not redundant with the container clause. An MP4 carrying
 * E-AC-3 is a real thing these sources serve, it is a perfectly legal MP4, and
 * it is silent on an iPhone — which is exactly the failure `remux.ts` exists to
 * prevent, container or no container. Anything that is not provably fine goes
 * through the remuxer.
 */
function canCopyStraight(probe: ProbedSource): boolean {
  if (probe.container !== 'mp4') return false;
  if (!devicePlayableVideo(probe)) return false;
  // No audio at all is fine — a silent MP4 is still a playable MP4.
  return probe.tracks.filter((t) => t.kind === 'audio').every((t) => t.codec === 'aac');
}

/** Everything about WHERE this download lands, resolved once in `run`. */
interface Landing {
  how: destination.Strategy;
  /** The `<Title>/Season N/` directory on the 'folder' strategy; null on 'save'. */
  into: FileSystemDirectoryHandle | null;
  name: string;
  savedPath: string;
  sizeBytes: number;
}

/**
 * CONVERT IT, IN A WORKER, STRAIGHT INTO THE DESTINATION FILE.
 *
 * The runner's part is small on purpose: pick the audio track, open the
 * destination, start the worker, and translate its progress into the same
 * `entry.bytes` the byte-copy path wrote. Everything about Matroska, fMP4 and
 * AAC lives in `remux.worker.ts` and `remux.ts`.
 *
 * THE AUDIO TRACK IS CHOSEN BY THE PLAYER'S OWN RULE. `preferredAudioAvailability`
 * is the same function the watch screen calls, reading the same
 * `prefs.audioLang`, so the file gets the language the reader would have heard
 * had they pressed Play — and an episode downloaded for a flight does not turn
 * out to be in Russian because the release's default flag said so.
 *
 * NO RESUME, AND THE INDEX MUST NOT PRETEND OTHERWISE. A remux always starts
 * from 0 and `entry.bytes` is reset to 0 before the worker opens the file: the
 * output's length is not a prefix of anything, so `resumeOffset` must never see
 * it. The worker truncates a cancelled file away for the same reason.
 */
async function remux(
  key: string,
  entry: DownloadEntry,
  candidate: Candidate,
  probe: ProbedSource & { direct?: unknown; mkv?: unknown },
  landing: Landing,
  signal: AbortSignal
): Promise<void> {
  const { how, into, name, savedPath, sizeBytes } = landing;
  const header = (probe as { mkv?: import('$lib/media/matroska').MkvHeader }).mkv;
  if (!header) {
    throw new Error('Jovii could not read that file well enough to convert it.');
  }

  /*
   * THE QUOTA CHECK IS AN ESTIMATE, AND SAYS SO.
   *
   * It is made against the SOURCE's size, because that is the only number
   * anybody has before the conversion runs. The output is normally a little
   * SMALLER — the video is copied byte for byte, and 5.1 E-AC-3 at 640 kbps
   * becomes stereo AAC at 192 — so the estimate errs the safe way. It is not a
   * guarantee: a file whose audio was already AAC comes out within a rounding
   * error of its source, and the headroom in `quotaVerdict` is what absorbs
   * that.
   */
  if (how === 'save') {
    const verdict = quotaVerdict(await store.estimate(), sizeBytes);
    if (!verdict.fits) {
      patch(key, { state: 'failed', error: verdict.message });
      say(verdict.message);
      return;
    }
  }

  const audioTrackNumber = preferredAudioAvailability(probe, prefs.audioLang).audioIndex;
  /* `durationSec` is only here so the status line's "12:34 of 51:20" has a
     total. The probe reads it off the container; 0 means the header did not
     say, and the screen shows the elapsed figure alone rather than a guess. */
  patch(key, {
    size: sizeBytes,
    bytes: 0,
    convertedSec: 0,
    durationSec: probe.durationSec,
    candidateId: candidate.id,
    state: 'downloading'
  });

  const target = into
    ? await (async () => {
        const handle = await store.folderFileHandle(into, name);
        return handle ? ({ kind: 'folder', handle } as const) : null;
      })()
    : ({ kind: 'opfs', key } as const);
  if (!target) throw new Error('Jovii could not open that file to write to.');

  const worker = new Worker(new URL('./remux.worker.ts', import.meta.url), {
    type: 'module',
    name: 'jovi-remux'
  });

  try {
    const outcome = await new Promise<{ ok: boolean; message?: string; cancelled?: boolean }>((resolve) => {
      const stop = (): void => worker.postMessage({ t: 'cancel' });
      signal.addEventListener('abort', stop, { once: true });
      worker.onmessage = (ev: MessageEvent<RemuxEvent>) => {
        const msg = ev.data;
        if (msg.t === 'progress') {
          /* `bytesIn` onto `entry.bytes`, so the ring and "X of Y" keep working
             unchanged. `secondsOut` rides alongside it for the status line,
             which counts minutes of film rather than megabytes during a
             conversion — see Downloads.svelte. */
          patch(key, {
            bytes: Math.min(msg.bytesIn, sizeBytes),
            convertedSec: msg.secondsOut,
            ...(msg.bytesTotal > 0 ? { size: msg.bytesTotal } : {})
          });
          return;
        }
        if (msg.t === 'needToken') {
          void renewCandidateUrl(entry.ref, candidate.id).then((token) =>
            worker.postMessage({ t: 'tokenReply', requestId: msg.requestId, token })
          );
          return;
        }
        signal.removeEventListener('abort', stop);
        if (msg.t === 'done') resolve({ ok: true });
        else if (msg.t === 'cancelled') resolve({ ok: false, cancelled: true });
        else resolve({ ok: false, message: msg.message });
      };
      worker.onerror = (ev) => {
        signal.removeEventListener('abort', stop);
        resolve({ ok: false, message: ev.message || 'The converter stopped unexpectedly.' });
      };
      worker.postMessage(
        {
          t: 'start',
          token: candidate.url,
          direct: (probe as { direct?: unknown }).direct !== undefined,
          header,
          sizeBytes,
          audioTrackNumber,
          into: target
        },
        target.kind === 'folder' ? [] : []
      );
    });

    if (outcome.cancelled || signal.aborted) {
      // The worker deleted its partial output; the row goes back to waiting
      // with nothing on disk, which is what it actually has.
      patch(key, { bytes: 0, convertedSec: 0 });
      return;
    }
    if (!outcome.ok) throw new Error(outcome.message ?? 'The conversion stopped.');
  } finally {
    worker.terminate();
  }

  const finalBytes = into ? await store.folderFileBytes(into, name) : ((await store.fileBytes(key)) ?? 0);
  await finish(key, candidate.id, landing, finalBytes > 0, finalBytes);
}

/**
 * DELIVERY, and the one write that says a download is over.
 *
 * On 'folder' the file is already where the reader asked for it — there is
 * nothing left to do but say so. On 'save' the completed staging copy is handed
 * to the browser's own download manager (an <a download> on an object URL, the
 * only route iOS offers), which puts it in the Files app.
 *
 * The hand-off needs a document, so it cannot happen if the tab has gone. That
 * is why the entry is only marked done AFTER it is handed over: a download
 * whose bytes are complete but which never reached the Files app is not
 * finished, and pressing it again re-delivers from the staged copy without
 * re-fetching a byte. The staged bytes are NOT deleted here — Safari reads a
 * blob download lazily and deleting it mid-transfer is the WebKitBlobResource
 * failure; `sweepDelivered` reclaims it on a later visit.
 *
 * `bytes` is set to the SOURCE size on success rather than the file's own
 * length, because that is what `size` holds and what `isComplete` compares
 * against — a remuxed file is normally smaller than its source, and reporting
 * its real length would leave every finished conversion looking like 94%.
 */
async function finish(
  key: string,
  candidateId: string,
  landing: Landing,
  complete: boolean,
  finalBytes: number
): Promise<void> {
  const { how, name, savedPath, sizeBytes } = landing;
  if (complete && how === 'save') {
    const file = await store.stagedFile(key);
    if (file) destination.handOff(file, name);
  }
  patch(key, {
    bytes: complete ? sizeBytes : finalBytes,
    size: sizeBytes,
    state: complete ? 'done' : 'queued',
    candidateId,
    ...(complete
      ? { savedVia: how, savedAs: name, ...(savedPath ? { savedPath } : {}), savedAt: Date.now() }
      : {})
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Playback
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The bytes of a completed download, for the player. Null when there is no
 * complete file — which is the signal to go to the network exactly as before.
 */
export async function localBytesFor(
  ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>
): Promise<import('./types').LocalByteSource | null> {
  const entry = completedDownload(ref);
  if (!entry) return null;
  return store.localSource(entry.key, entry.size);
}

/**
 * RECLAIM THE STAGED COPIES, LATER.
 *
 * On the 'save' strategy a finished film exists twice for a while: once in the
 * browser's private storage (where it was assembled) and once wherever the
 * browser's download manager put it. The staged copy has to survive the
 * hand-off — Safari reads a blob download lazily and deleting it mid-transfer
 * is what produced "WebKitBlobResource error 1" on the owner's phone — but it
 * must not survive forever, or the second film has nowhere to go.
 *
 * So the sweep runs on the NEXT visit rather than at delivery. By the time the
 * app is opened again, any download that was going to read those bytes has
 * either finished or been abandoned, and a grace window makes that explicit
 * rather than assumed. The entry keeps its history row: the file is in Files
 * now, and this list stopped being a library when it became a history.
 */
const DELIVERY_GRACE_MS = 30 * 60 * 1000;

export async function sweepDelivered(now = Date.now()): Promise<void> {
  if (!store.supported()) return;
  for (const entry of downloads.items) {
    if (entry.state !== 'done' || entry.savedVia !== 'save') continue;
    if (entry.savedAt === undefined || now - entry.savedAt < DELIVERY_GRACE_MS) continue;
    const bytes = await store.fileBytes(entry.key);
    if (bytes === null) continue;
    await store.removeFile(entry.key);
  }
}
